import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSelector } from "./LanguageSelector";
import { useI18n } from "@/i18n/I18nContext";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  const links = [
    { to: "/", key: "home", end: true },
    { to: "/a-propos", key: "about" },
    { to: "/fonctionnement", key: "how" },
    { to: "/fiches-digitales", key: "digital" },
    { to: "/nfc", key: "nfc" },
    { to: "/tarifs", key: "pricing" },
    { to: "/faq", key: "faq" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-strong" data-testid="site-navbar">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 h-16 flex items-center gap-4">
        <Link to="/" data-testid="nav-logo" onClick={() => setOpen(false)} className="shrink-0">
          <Logo size="text-xl" />
        </Link>

        <nav className="hidden lg:flex flex-1 items-center justify-center gap-0.5 xl:gap-1 2xl:gap-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `px-2 xl:px-3 py-2 text-sm whitespace-nowrap rounded-lg transition-colors duration-300 ${
                  isActive ? "text-white bg-white/5" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex shrink-0 items-center gap-2 xl:gap-3">
          <LanguageSelector />
          <Link to="/connexion" data-testid="nav-login" className="text-sm whitespace-nowrap text-zinc-300 hover:text-white transition-colors duration-300 px-1 xl:px-2">
            {t("nav.clientArea")}
          </Link>
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="group ml-1 xl:ml-2 inline-flex items-center gap-1.5 bg-brand-gradient text-white text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap hover:opacity-90 transition-opacity duration-300"
          >
            {t("nav.contact")}
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </Link>
        </div>

        <div className="lg:hidden ml-auto flex items-center gap-1">
          <LanguageSelector />
          <button className="text-white p-2" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label={t("nav.menu")}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-strong border-t border-white/10 px-5 py-4" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-base ${isActive ? "text-white bg-white/5" : "text-zinc-300"}`
                }
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
            <div className="h-px bg-white/10 my-2" />
            <button onClick={() => { setOpen(false); navigate("/connexion"); }} className="px-3 py-3 text-left text-zinc-300">
              {t("nav.clientArea")}
            </button>
            <button onClick={() => { setOpen(false); navigate("/contact"); }} className="bg-brand-gradient text-white font-semibold px-4 py-3 rounded-full mt-1">
              {t("nav.contact")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
